/**
 * Language addresses (/de/, /fr/, /es/, /zh-cn/).
 *
 * The English site keeps its existing URLs exactly as they are. Each other
 * language is served from its own prefixed address so search engines can index
 * it separately, and so a person can share "the German page" as the German page.
 *
 * The prefix is stripped before routing (see src/server.ts), so no route file is
 * duplicated: /de/grounding renders the same page as /grounding, translated.
 */

export const LOCALES = ["en", "de", "fr", "es", "nl", "zh-cn", "ar", "hi"] as const;
export type Locale = (typeof LOCALES)[number];

/** Locales that carry a URL prefix. English is the unprefixed original. */
export const PREFIXED_LOCALES = LOCALES.filter((l) => l !== "en") as Exclude<Locale, "en">[];

/** Value used in hreflang and the <html lang> attribute. */
export const HREFLANG: Record<Locale, string> = {
  en: "en",
  de: "de",
  fr: "fr",
  es: "es",
  nl: "nl",
  "zh-cn": "zh-Hans",
  ar: "ar",
  hi: "hi",
};

/** Open Graph locale codes, which use a region suffix. */
export const OG_LOCALE: Record<Locale, string> = {
  en: "en_US",
  de: "de_DE",
  fr: "fr_FR",
  es: "es_ES",
  nl: "nl_NL",
  "zh-cn": "zh_CN",
  ar: "ar_AR",
  hi: "hi_IN",
};

export const LOCALE_LABELS: Record<Locale, { label: string; short: string; flag: string }> = {
  en: { label: "English", short: "EN", flag: "🇬🇧" },
  de: { label: "Deutsch", short: "DE", flag: "🇩🇪" },
  fr: { label: "Français", short: "FR", flag: "🇫🇷" },
  es: { label: "Español", short: "ES", flag: "🇪🇸" },
  nl: { label: "Nederlands", short: "NL", flag: "🇳🇱" },
  "zh-cn": { label: "简体中文", short: "中文", flag: "🇨🇳" },
  ar: { label: "العربية", short: "AR", flag: "🇸🇦" },
  hi: { label: "हिन्दी", short: "HI", flag: "🇮🇳" },
};

/** Single preferred host — matches the canonical host redirect in src/start.ts. */
export const SITE_URL = "https://recoverytrauma.com";

/** Languages written right to left. */
export const RTL_LOCALES: readonly Locale[] = ["ar"];

export function isRtlLocale(locale: Locale): boolean {
  return RTL_LOCALES.includes(locale);
}

/** Text direction for a language, for the <html dir> attribute. */
export function localeDir(locale: Locale): "ltr" | "rtl" {
  return isRtlLocale(locale) ? "rtl" : "ltr";
}

export function isLocale(value: string | null | undefined): value is Locale {
  return !!value && (LOCALES as readonly string[]).includes(value);
}

/**
 * Split a pathname into its language prefix and the underlying English path.
 * "/de/grounding" -> { locale: "de", path: "/grounding" }
 * "/grounding"    -> { locale: "en", path: "/grounding" }
 */
export function splitLocalePath(pathname: string): { locale: Locale; path: string } {
  const match = /^\/([a-z]{2}(?:-[a-z]{2})?)(\/.*)?$/i.exec(pathname);
  if (match) {
    const candidate = match[1].toLowerCase();
    if (candidate !== "en" && isLocale(candidate)) {
      return { locale: candidate, path: match[2] && match[2] !== "" ? match[2] : "/" };
    }
  }
  return { locale: "en", path: pathname || "/" };
}

/** Build the address of an English path in a given language. */
export function localePath(locale: Locale, path: string): string {
  const clean = path.startsWith("/") ? path : `/${path}`;
  if (locale === "en") return clean;
  // Trailing slash on the home page keeps the address identical to the one the
  // app itself generates, so nothing shifts when the page becomes interactive.
  return clean === "/" ? `/${locale}/` : `/${locale}${clean}`;
}

/** Absolute URL of an English path in a given language. */
export function localeUrl(locale: Locale, path: string): string {
  return `${SITE_URL}${localePath(locale, path)}`;
}

/**
 * Paths that must never be served under a language prefix: API endpoints,
 * build assets, framework internals and the crawler files themselves.
 */
export function isTranslatablePath(path: string): boolean {
  if (
    /^\/(api|_serverFn|assets|src|node_modules|@|__|lovable|sw\.js|sitemap\.xml|robots\.txt|llms(-full)?\.txt|manifest\.webmanifest|favicon|icon-|apple-touch-icon|offline\.html)/i.test(
      path,
    )
  ) {
    return false;
  }
  // Anything that looks like a file (styles, images, downloads) is not a page.
  const last = path.split("?")[0].split("/").pop() ?? "";
  return !/\.[a-z0-9]{2,5}$/i.test(last);
}

/**
 * The language the person is currently reading, taken from the address bar.
 * Used for full-page jumps and payment return addresses, which are absolute
 * URLs and so do not inherit the router's language prefix.
 */
export function browserLocale(): Locale {
  if (typeof window === "undefined") return "en";
  return splitLocalePath(window.location.pathname).locale;
}

/**
 * Origin plus the current language prefix, e.g. "https://site.com/hi".
 * Handed to payment providers so a Hindi reader comes back to Hindi pages.
 */
export function localeOrigin(): string {
  const origin = typeof window !== "undefined" ? window.location.origin : SITE_URL;
  const locale = browserLocale();
  return locale === "en" ? origin : `${origin}/${locale}`;
}

/** Absolute address of a path in the language the person is reading. */
export function localeHref(path: string): string {
  const origin = typeof window !== "undefined" ? window.location.origin : SITE_URL;
  return `${origin}${localePath(browserLocale(), path)}`;
}

/** Same as localeHref but relative — for full-page jumps within the site. */
export function localeTo(path: string): string {
  return localePath(browserLocale(), path);
}
